import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  ApplyWfhInput,
  WfhAttendanceLogItem,
  WfhDashboardData,
  WfhHistoryResult,
  WfhRequestDetails,
  WfhRequestFilters,
  WfhRequestListItem,
  WfhSessionInfo,
  WfhSessionOptions,
  WfhTeamMember,
} from '../types/wfh';
import { durationOptionsFromPayload } from './leaveService';
import { calendarDateFromApi } from '../utils/dateTime';
import { normalizeSessionCode } from '../utils/leaveSession';
import {
  createDefaultWfhFilters,
  normalizeWfhFilters,
} from '../utils/wfhFilters';
import { resolveRequestCanCancel } from '../utils/requestCancelWindow';
import { getWfhStatusLabel } from '../utils/wfhStatus';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function toCamelKey(key: string): string {
  return key.replace(/_([a-z0-9])/gi, (_, ch: string) => ch.toUpperCase());
}

function camelizeRow(row: ObjectValue): ObjectValue {
  const flattened: ObjectValue = { ...row };
  for (const [key, value] of Object.entries(row)) {
    flattened[toCamelKey(key)] = value;
  }
  return flattened;
}

function objectRow(value: unknown): ObjectValue | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as ObjectValue;
}

function detailsRow(payload: ObjectValue): ObjectValue {
  const nestedRequest = objectRow(payload.request);
  const nestedWorkflow = objectRow(payload.workflow);
  const merged = camelizeRow({
    ...payload,
    ...(nestedWorkflow ?? {}),
    ...(nestedRequest ?? {}),
  });
  const cancellationReason =
    textOrNull(
      nestedRequest?.cancellationReason ??
        nestedRequest?.cancellation_reason ??
        nestedRequest?.cancelReason ??
        nestedRequest?.cancel_reason,
    ) ??
    textOrNull(
      payload.cancellationReason ??
        payload.cancellation_reason ??
        payload.cancelReason ??
        payload.cancel_reason,
    ) ??
    textOrNull(
      nestedWorkflow?.cancellationReason ??
        nestedWorkflow?.cancellation_reason ??
        nestedWorkflow?.cancelReason ??
        nestedWorkflow?.cancel_reason,
    );
  if (cancellationReason) {
    merged.cancellationReason = cancellationReason;
  }
  return merged;
}

function dateOnly(value: unknown): string | null {
  return calendarDateFromApi(value);
}

function reasonSummary(value: unknown): string | null {
  const text = textOrNull(value);
  if (!text) return null;
  return text.length > 80 ? `${text.slice(0, 77)}...` : text;
}

function booleanOrUndefined(value: unknown): boolean | undefined {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true;
  }
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false;
  }
  return undefined;
}

function isRequestCancellable(statusCode: string | null, row: ObjectValue): boolean {
  return resolveRequestCanCancel({
    statusCode,
    canCancelFromApi: booleanOrUndefined(
      row.canCancel ?? row.can_cancel ?? row.allowCancel ?? row.allow_cancel,
    ),
    fromDate: dateOnly(
      row.fromDate ??
        row.workFromDate ??
        row.startDate ??
        row.wfhFromDate,
    ),
    toDate: dateOnly(
      row.toDate ?? row.workToDate ?? row.endDate ?? row.wfhToDate,
    ),
  });
}

function sessionInfo(row: ObjectValue): WfhSessionInfo {
  return {
    sessionType: normalizeSessionCode(
      textOrNull(row.sessionType ?? row.session_type ?? row.sessionCode),
    ),
    sessionName: textOrNull(
      row.sessionName ?? row.session_name ?? row.sessionTypeName,
    ),
    durationName: textOrNull(row.durationName ?? row.duration_name),
    isHalfDay: booleanOrUndefined(row.isHalfDay ?? row.is_half_day) === true,
  };
}

function normalizeRequest(row: ObjectValue): WfhRequestListItem | null {
  const wfhRequestId = numberOrNull(
    row.wfhRequestId ?? row.wfh_request_id ?? row.requestId,
  );
  if (wfhRequestId === null || wfhRequestId <= 0) return null;

  const statusCode =
    textOrNull(row.statusCode) ?? textOrNull(row.status) ?? null;

  return {
    wfhRequestId,
    requestNumber: textOrNull(row.requestNumber ?? row.wfhRequestNumber),
    fromDate: dateOnly(
      row.fromDate ??
        row.workFromDate ??
        row.startDate ??
        row.wfhFromDate,
    ),
    toDate: dateOnly(
      row.toDate ?? row.workToDate ?? row.endDate ?? row.wfhToDate,
    ),
    totalDays: numberOrNull(
      row.totalDays ?? row.wfhDays ?? row.numberOfDays ?? row.durationDays,
    ),
    requestType: textOrNull(
      row.requestType ?? row.wfhType ?? row.wfhRequestType,
    ),
    statusCode,
    statusLabel: getWfhStatusLabel(statusCode),
    applicationDate: dateOnly(
      row.applicationDate ??
        row.requestDate ??
        row.appliedOn ??
        row.createdAt,
    ),
    reasonSummary: reasonSummary(row.reason ?? row.wfhReason ?? row.remarks),
    canCancel: isRequestCancellable(statusCode, row),
    ...sessionInfo(row),
  };
}

function normalizeDetails(row: ObjectValue): WfhRequestDetails | null {
  const base = normalizeRequest(row);
  if (!base) return null;

  const workflowInstanceId = numberOrNull(
    row.workflowInstanceId ?? row.workflowId,
  );
  const currentStepName = textOrNull(
    row.currentStepName ?? row.workflowStepName,
  );
  const currentApproverName = textOrNull(
    row.currentApproverName ?? row.approverName,
  );
  const hasWorkflow =
    workflowInstanceId !== null ||
    currentStepName !== null ||
    currentApproverName !== null;

  return {
    wfhRequestId: base.wfhRequestId,
    requestNumber: base.requestNumber,
    applicationDate: base.applicationDate,
    fromDate: base.fromDate,
    toDate: base.toDate,
    totalDays: base.totalDays,
    requestType: base.requestType,
    sessionType: base.sessionType,
    sessionName: base.sessionName,
    durationName: base.durationName,
    isHalfDay: base.isHalfDay,
    statusCode: base.statusCode,
    statusLabel: base.statusLabel,
    reason: textOrNull(row.reason ?? row.wfhReason ?? row.remarks),
    workLocation: textOrNull(row.workLocation ?? row.location),
    contactNumber: textOrNull(row.contactNumber ?? row.contactPhone),
    availabilityNotes: textOrNull(
      row.availabilityNotes ?? row.availability ?? row.notes,
    ),
    submittedAt: textOrNull(
      row.submittedAt ?? row.appliedAt ?? row.appliedOn ?? row.createdAt,
    ),
    rejectReason: textOrNull(
      row.rejectReason ?? row.rejectionReason ?? row.rejectedReason,
    ),
    cancellationReason: textOrNull(
      row.cancellationReason ??
        row.cancelReason ??
        row.cancelledReason ??
        row.canceledReason ??
        row.cancellation_reason ??
        row.cancel_reason,
    ),
    employee: {
      employeeId: numberOrNull(
        row.employeeId ?? row.requesterEmployeeId ?? row.applicantEmployeeId,
      ),
      displayName: textOrNull(
        row.displayName ??
          row.requesterName ??
          row.employeeName ??
          row.requesterDisplayName,
      ),
      employeeNumber: textOrNull(
        row.employeeNumber ?? row.requesterEmployeeNumber,
      ),
      designationName: textOrNull(
        row.designationName ?? row.requesterDesignationName,
      ),
    },
    workflow: hasWorkflow
      ? {
          workflowInstanceId,
          currentStepName,
          currentApproverName,
        }
      : null,
    canCancel: base.canCancel,
  };
}

function unwrapRequestRow(
  payload: { request?: ObjectValue | null } | ObjectValue,
): ObjectValue {
  if (
    payload &&
    typeof payload === 'object' &&
    'request' in payload &&
    payload.request &&
    typeof payload.request === 'object' &&
    !Array.isArray(payload.request)
  ) {
    return payload.request as ObjectValue;
  }
  return (payload ?? {}) as ObjectValue;
}

function detailsFromApproveResponse(
  payload: { request?: ObjectValue | null } | ObjectValue,
  wfhRequestId: number,
  statusCode: string,
): WfhRequestDetails {
  const details = normalizeDetails(unwrapRequestRow(payload));
  if (details) return details;
  return {
    wfhRequestId,
    requestNumber: null,
    applicationDate: null,
    fromDate: null,
    toDate: null,
    totalDays: null,
    requestType: null,
    sessionType: null,
    sessionName: null,
    durationName: null,
    isHalfDay: false,
    statusCode,
    statusLabel: getWfhStatusLabel(statusCode),
    reason: null,
    workLocation: null,
    contactNumber: null,
    availabilityNotes: null,
    submittedAt: null,
    rejectReason: null,
    cancellationReason: null,
    employee: {
      employeeId: null,
      displayName: null,
      employeeNumber: null,
      designationName: null,
    },
    workflow: null,
    canCancel: false,
  };
}

export async function getWfhDashboard(options?: {
  fromDate?: string;
  toDate?: string;
}): Promise<WfhDashboardData> {
  const params: Record<string, string> = {};
  if (options?.fromDate) params.fromDate = options.fromDate;
  if (options?.toDate) params.toDate = options.toDate;

  const response = await apiClient.get<
    Envelope<{
      statistics?: ObjectValue;
      activeRequest?: ObjectValue | null;
      recentRequests?: ObjectValue[];
    }>
  >(API_ENDPOINTS.WFH.DASHBOARD, {
    params: Object.keys(params).length ? params : undefined,
  });

  const data = response.data.data ?? {};
  const statistics = data.statistics ?? {};
  const recent = Array.isArray(data.recentRequests)
    ? data.recentRequests
        .map(normalizeRequest)
        .filter((item): item is WfhRequestListItem => item !== null)
    : [];

  return {
    statistics: {
      totalRequests: numberOrNull(
        statistics.totalRequests ?? statistics.totalWfhRequests,
      ),
      approvedRequests: numberOrNull(
        statistics.approvedRequests ?? statistics.approvedCount,
      ),
      pendingRequests: numberOrNull(
        statistics.pendingRequests ?? statistics.pendingCount,
      ),
    },
    activeRequest: data.activeRequest
      ? normalizeRequest(data.activeRequest as ObjectValue)
      : null,
    recentRequests: recent,
  };
}

export async function getWfhRequests(
  options: {
    filters?: Partial<WfhRequestFilters>;
    page?: number;
    pageSize?: number;
  } = {},
): Promise<WfhHistoryResult> {
  const defaults = createDefaultWfhFilters();
  const normalized = normalizeWfhFilters({
    fromDate: options.filters?.fromDate ?? defaults.fromDate,
    toDate: options.filters?.toDate ?? defaults.toDate,
    page: options.page ?? 1,
    pageSize: options.pageSize ?? 20,
  });

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: unknown;
        pageSize?: unknown;
        totalRecords?: unknown;
        totalPages?: unknown;
      };
    }>
  >(API_ENDPOINTS.WFH.HISTORY, {
    params: {
      fromDate: normalized.filters.fromDate,
      toDate: normalized.filters.toDate,
      page: normalized.page,
      pageSize: normalized.pageSize,
    },
  });

  const data = response.data.data ?? {};
  const pagination = data.pagination ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeRequest)
        .filter((item): item is WfhRequestListItem => item !== null)
    : [];
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const pageSize = numberOrNull(pagination.pageSize) ?? normalized.pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / Math.max(pageSize, 1)));

  return {
    items,
    page: numberOrNull(pagination.page) ?? normalized.page,
    pageSize,
    totalItems,
    totalPages,
  };
}

export async function getWfhRequestDetails(
  wfhRequestId: number,
): Promise<WfhRequestDetails> {
  if (!Number.isInteger(wfhRequestId) || wfhRequestId <= 0) {
    throw Object.assign(new Error('wfhRequestId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.get<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.WFH.REQUEST_DETAILS(wfhRequestId));

  const payload = response.data.data ?? {};
  const details = normalizeDetails(detailsRow((payload ?? {}) as ObjectValue));
  if (!details) {
    throw Object.assign(new Error('WFH request was not found.'), {
      code: 'WFH_REQUEST_NOT_FOUND',
    });
  }
  return details;
}

export async function getWfhAttendanceLog(
  wfhRequestId: number,
): Promise<WfhAttendanceLogItem[]> {
  if (!Number.isInteger(wfhRequestId) || wfhRequestId <= 0) {
    throw Object.assign(new Error('wfhRequestId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.get<
    Envelope<{ items?: ObjectValue[] } | ObjectValue[]>
  >(API_ENDPOINTS.WFH.ATTENDANCE_LOG(wfhRequestId));

  const payload = response.data.data;
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { items?: ObjectValue[] } | null)?.items)
      ? ((payload as { items: ObjectValue[] }).items)
      : [];

  return rows.map(row => ({
    wfhAttendanceLogId: numberOrNull(
      row.wfhAttendanceLogId ?? row.attendanceLogId,
    ),
    attendanceDate: dateOnly(row.attendanceDate),
    checkInAt: textOrNull(row.checkInAt ?? row.firstCheckInAt),
    checkOutAt: textOrNull(row.checkOutAt ?? row.lastCheckOutAt),
    createdAt: textOrNull(row.createdAt),
  }));
}

/** Full Day / Half Day options allowed by the employee's WFH policy. */
export async function getWfhSessionTypes(
  date?: string | null,
  employeeId?: number | null,
): Promise<WfhSessionOptions> {
  const params: Record<string, string | number> = {};
  if (date) params.date = date;
  if (Number.isInteger(employeeId) && (employeeId as number) > 0) {
    params.employeeId = employeeId as number;
  }
  const response = await apiClient.get<
    Envelope<{
      policy?: ObjectValue | null;
      halfDayAllowed?: unknown;
      items?: ObjectValue[];
      advanceNoticeWaived?: unknown;
    }>
  >(API_ENDPOINTS.WFH.SESSION_TYPES, {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  const data = response.data.data ?? {};
  const policy = objectRow(data.policy);
  return {
    policyName: textOrNull(policy?.policyName),
    halfDayAllowed: booleanOrUndefined(data.halfDayAllowed) === true,
    maximumDaysPerMonth: numberOrNull(policy?.maximumDaysPerMonth),
    minimumNoticeDays: numberOrNull(
      data.minimumNoticeDays ?? policy?.minimumNoticeDays,
    ),
    serverDate: dateOnly(data.serverDate),
    advanceNoticeWaived: booleanOrUndefined(data.advanceNoticeWaived) === true,
    durations: durationOptionsFromPayload(data as ObjectValue),
  };
}

/** Assigned employees whose name matches the signed-in team lead's search. */
export async function getWfhTeamMembers(
  search: string,
): Promise<WfhTeamMember[]> {
  const query = search.trim();
  if (query.length < 3) return [];
  const response = await apiClient.get<Envelope<{ items?: ObjectValue[] }>>(
    API_ENDPOINTS.WFH.TEAM_MEMBERS,
    { params: { search: query } },
  );
  const items = Array.isArray(response.data.data?.items)
    ? response.data.data.items
    : [];
  return items.flatMap(row => {
    const source = camelizeRow(row);
    const employeeId = numberOrNull(source.employeeId);
    if (employeeId === null || employeeId <= 0) return [];
    return [
      {
        employeeId,
        displayName: textOrNull(source.displayName),
        employeeNumber: textOrNull(source.employeeNumber),
        departmentName: textOrNull(source.departmentName),
        designationName: textOrNull(source.designationName),
      },
    ];
  });
}

export async function applyWfhRequest(
  input: ApplyWfhInput,
): Promise<WfhRequestDetails> {
  const reason = textOrNull(input.reason);
  if (!reason) {
    throw Object.assign(new Error('reason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const body: Record<string, unknown> = {
    fromDate: input.fromDate,
    toDate: input.toDate,
    reason,
    workLocation: textOrNull(input.workLocation) ?? undefined,
    sessionType: normalizeSessionCode(input.sessionType) ?? undefined,
    expectedStartTime: textOrNull(input.expectedStartTime) ?? undefined,
    expectedEndTime: textOrNull(input.expectedEndTime) ?? undefined,
  };
  if (input.totalDays != null && Number.isFinite(Number(input.totalDays))) {
    body.totalDays = Number(input.totalDays);
  }
  if (Number.isInteger(input.employeeId) && (input.employeeId as number) > 0) {
    body.employeeId = input.employeeId;
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.WFH.APPLY, body);

  const payload = response.data.data ?? {};
  const row =
    payload &&
    typeof payload === 'object' &&
    'request' in payload &&
    payload.request &&
    typeof payload.request === 'object'
      ? (payload.request as ObjectValue)
      : (payload as ObjectValue);

  const details = normalizeDetails(row);
  if (!details) {
    throw Object.assign(new Error('WFH request could not be created.'), {
      code: 'WFH_APPLY_FAILED',
    });
  }
  return details;
}

export async function cancelWfhRequest(
  wfhRequestId: number,
  reason: string,
): Promise<WfhRequestDetails> {
  if (!Number.isInteger(wfhRequestId) || wfhRequestId <= 0) {
    throw Object.assign(new Error('wfhRequestId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  const cancellationReason = textOrNull(reason);
  if (!cancellationReason) {
    throw Object.assign(new Error('cancellationReason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.WFH.CANCEL(wfhRequestId), {
    cancellationReason,
    reason: cancellationReason,
  });

  const payload = response.data.data ?? {};
  const details = normalizeDetails(detailsRow((payload ?? {}) as ObjectValue));
  if (!details) {
    throw Object.assign(new Error('WFH request was not found.'), {
      code: 'WFH_REQUEST_NOT_FOUND',
    });
  }
  return details;
}

export async function approveWfhRequest(
  wfhRequestId: number,
): Promise<WfhRequestDetails> {
  if (!Number.isInteger(wfhRequestId) || wfhRequestId <= 0) {
    throw Object.assign(new Error('wfhRequestId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.WFH.APPROVE(wfhRequestId));

  return detailsFromApproveResponse(response.data.data ?? {}, wfhRequestId, 'APPROVED');
}

export async function rejectWfhRequest(
  wfhRequestId: number,
  reason: string,
): Promise<WfhRequestDetails> {
  if (!Number.isInteger(wfhRequestId) || wfhRequestId <= 0) {
    throw Object.assign(new Error('wfhRequestId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  const rejectionReason = textOrNull(reason);
  if (!rejectionReason) {
    throw Object.assign(new Error('rejectionReason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.WFH.REJECT(wfhRequestId), { rejectionReason });

  return detailsFromApproveResponse(response.data.data ?? {}, wfhRequestId, 'REJECTED');
}
